import React, { useEffect, useMemo, useState } from 'react';
import pluralize from 'pluralize';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';

import { type ILead, type ILeadLabel, useUpdateLeads } from 'src/services/api';
import { FlexColumn } from 'src/components/flex';
import SelectLeadLabels from 'src/components/input/select-lead-labels/SelectLeadLabels';

type Props = {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    leads: ILead[];
};

const getLabelsKey = (labels: ILeadLabel[]) => labels
    .map((label) => label.id)
    .sort((a, b) => a - b)
    .join(',');

export default function BulkChangeLeadLabelModal({
    open,
    onClose,
    onSuccess,
    leads,
}: Props) {
    const {
        mutateAsync: updateLeads,
        isLoading: isUpdatingLeads,
    } = useUpdateLeads();

    const areAllLeadsSameLabels = useMemo(() => {
        if (!leads.length) {
            return false;
        }

        const labelKeys = leads.map((lead) => getLabelsKey(lead.labels));
        return new Set(labelKeys).size === 1;
    }, [leads]);

    const initialLabels = useMemo(() => {
        if (!leads.length || !areAllLeadsSameLabels) {
            return [];
        }

        return leads[0].labels;
    }, [areAllLeadsSameLabels, leads]);

    const [selectedLabels, setSelectedLabels] = useState<ILeadLabel[]>([]);
    const [hasTouched, setHasTouched] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }

        setSelectedLabels(initialLabels);
        setHasTouched(false);
    }, [open, initialLabels]);

    const handleChange = (value: ILeadLabel[]) => {
        setSelectedLabels(value);
        setHasTouched(true);
    };

    const save = async () => {
        if (!hasTouched) {
            return;
        }

        await updateLeads(leads.map((lead) => ({
            uuid: lead.uuid,
            labels: selectedLabels,
        })));

        onSuccess();
        onClose();
    };

    const selectKey = useMemo(() => {
        return `bulk-change-lead-labels-${open ? 'open' : 'closed'}-${getLabelsKey(initialLabels)}-${leads.length}`;
    }, [open, initialLabels, leads.length]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: '100%' } }}
        >
            <DialogTitle>Change Labels</DialogTitle>
            <DialogContent>
                <FlexColumn rowGap={2} pt={1}>
                    <SelectLeadLabels
                        key={selectKey}
                        label="Labels"
                        lead={areAllLeadsSameLabels ? leads[0] : undefined}
                        onChange={handleChange}
                        data-testid="bulk-change-labels-select"
                    />

                    <DialogContentText sx={{ color: 'red', fontSize: 'small' }}>
                        This action will overwrite the existing labels for {leads.length} {pluralize('lead', leads.length)}.
                    </DialogContentText>
                </FlexColumn>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="outlined"
                    color="dark"
                    onClick={onClose}
                    disabled={isUpdatingLeads}
                    data-testid="bulk-change-labels-cancel-button"
                >
                    Cancel
                </Button>

                <LoadingButton
                    variant="contained"
                    onClick={save}
                    disabled={!hasTouched}
                    loading={isUpdatingLeads}
                    data-testid="bulk-change-labels-save-button"
                >
                    Save
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
